import { startsWith, transform } from 'lodash'
import { currentPageParams } from 'scrivito'

import { writeUrlParams, type UrlParamsPatch } from '@/hooks/useUrlParams'
import { ensureString } from '@/utils/ensureString'

export const FILTER_PARAM_PREFIX = 'f_'

export type FilterValues = Record<string, string>

/**
 * Serialize filter values to `f_*` URL params.
 */
export const filtersToParams = (
  filters: FilterValues,
): Record<string, string> =>
  transform(
    filters,
    (params: Record<string, string>, value: string, key: string) => {
      params[FILTER_PARAM_PREFIX + key] = value
    },
    {} as Record<string, string>,
  )

/**
 * Deserialize URL params to filter values — only `f_*` keys, prefix stripped.
 */
export const paramsToFilters = (params: {
  [key: string]: string | (string | null)[] | null
}): FilterValues =>
  transform(
    params,
    (
      filters: FilterValues,
      value: string | (string | null)[] | null,
      key: string,
    ) => {
      if (startsWith(key, FILTER_PARAM_PREFIX)) {
        filters[key.substring(FILTER_PARAM_PREFIX.length)] = ensureString(value)
      }
    },
    {} as FilterValues,
  )

/**
 * Current committed filters from the URL. Call only inside `connect()`
 * (currentPageParams() is reactive there).
 */
export const readCommittedFilters = (): FilterValues =>
  paramsToFilters(currentPageParams())

/**
 * Number of filters the user sees as active in `values`. A date range writes a
 * `.gte`/`.lte` pair but counts once, as one visible filter.
 */
export const getActiveAttributeFilterCount = (values: FilterValues): number => {
  const activeFilterGroups = new Set(
    Object.keys(values).map((key) => key.replace(/\.(gte|lte)$/, '')),
  )

  return activeFilterGroups.size
}

/**
 * Replace ALL committed filters with `values` in ONE navigation: null every
 * existing `f_*` key, then write the new set. Single commit path used by both
 * the inline write-through (`useFilterValues` fallback) and the staged Apply
 * (`FormContextProvider` commit).
 */
export const writeFilters = (values: FilterValues): void => {
  const patch: UrlParamsPatch = {}
  for (const k of Object.keys(currentPageParams())) {
    if (startsWith(k, FILTER_PARAM_PREFIX)) {
      patch[k] = null
    }
  }
  Object.assign(patch, filtersToParams(values))
  writeUrlParams(patch)
}
